<template>
  <q-header
    data-test-id="consumer-header"
    :reveal="!currentSite.consumerConfig.stickyHeader && !stickyHeader"
    :class="{
      'bg-gradient-transparent': currentSite.consumerConfig.headerBgTransparent,
      'text-white': $isColorDark(currentSite.headerColor),
      'text-black': !$isColorDark(currentSite.headerColor)
    }"
  >
    <slot name="header-top" />
    <header-top-bar
      v-if="showTopBar && showOnScrollUp"
      :set-styles="styles"
      :show-change-location-button="showChangeLocationButton"
      @site-selector-shown="shown => shown && closeMainMenu()"
      @show-site-selector-drawer="$emit('show-site-selector-drawer'); closeMainMenu()"
    />
    <transition
      enter-active-class="animated fadeInDown"
      leave-active-class="animated fadeOutUp"
    >
      <center-align-header
        v-if="currentSite.consumerConfig.headerLogoCentered && currentSite.consumerConfig.headerType !== HEADER_TYPES.DEFAULT && showOnScrollUp"
        class="row justify-between items-center"
        style="margin-left: auto; margin-right: auto; width: 100%;"
        :style="styles"
        :logged-in="loggedIn"
        :orders="orders"
        :menu-list="menuList"
        transition-show="slide-down"
        transition-hide="slide-up"
        @toggle-main-menu="toggleMainMenu"
        @search="$emit('search')"
      />

      <left-align-header
        v-else-if="showOnScrollUp"
        :class="currentSite.consumerConfig.headerStackedText ? 'justify-between' : 'justify-end'"
        style="margin-left: auto; margin-right: auto;"
        :style="styles"
        :logged-in="loggedIn"
        :orders="orders"
        :show-change-location-button="showChangeLocationButton"
        :has-other-icons="hasOtherIcons"
        :menu-list="menuList"
        @toggle-main-menu="toggleMainMenu"
        @search="$emit('search')"
        @site-selector-shown="shown => shown && closeMainMenu()"
        @show-site-selector-drawer="$emit('show-site-selector-drawer'); closeMainMenu()"
      />
    </transition>
    <transition
      enter-active-class="animated slideInDown"
      leave-active-class="animated slideOutUp"
    >
      <header-bottom-bar
        v-if="currentSite.consumerConfig.headerType == HEADER_TYPES.STACK && $q.screen.gt.xs && showOnScrollUp"
        :class="currentSite.consumerConfig.headerLogoCentered ? 'justify-center' : 'justify-start'"
        style="margin-left: auto; margin-right: auto;"
        :style="styles"
        :show-change-location-button="showChangeLocationButton"
        :has-other-icons="hasOtherIcons"
      />
    </transition>
    <slot name="header-bottom" />
    <mobile-navigations-drawer
      v-if="menuList.length"
      v-model="drawerShown"
      :menu-list="menuList"
      :user-dark-mode="useDarkMode"
      :logged-in="loggedIn"
      @search="$emit('search')"
    />
    <consumer-header-edit-menu
      v-if="currentUser && currentUser.admin"
      @edit-navigations="$emit('edit-navigations')"
      @edit-pages="$emit('edit-pages')"
      @edit-site-css="$emit('edit-site-css')"
      @time-travel="$emit('time-travel')"
      @edit-header-style="$emit('edit-header-style')"
    />
    <SiteSwitchProgressBar style="position: absolute; top: calc(100% - 3px); left: 0; right: 0;" />
  </q-header>
</template>

<script>
import { groupByCategory } from 'src/models/page/Navigation'
import { HEADER_TYPES } from 'src/models/site/Site'
import SiteSwitchProgressBar from 'src/modules/consumer/components/SiteSwitchProgressBar'
import { defineAsyncComponent } from 'vue'

export default {
  name: 'ConsumerHeader',
  components: {
    ConsumerHeaderEditMenu: defineAsyncComponent(() => import('src/modules/consumer/components/ConsumerHeaderEditMenu')),
    HeaderTopBar: defineAsyncComponent(() => import('src/modules/consumer/components/header/HeaderTopBar')),
    CenterAlignHeader: defineAsyncComponent(() => import('src/modules/consumer/components/header/CenterAlignHeader')),
    LeftAlignHeader: defineAsyncComponent(() => import('src/modules/consumer/components/header/LeftAlignHeader')),
    HeaderBottomBar: defineAsyncComponent(() => import('src/modules/consumer/components/header/HeaderBottomBar')),
    MobileNavigationsDrawer: defineAsyncComponent(() => import('src/modules/consumer/components/header/MobileNavigationsDrawer')),
    SiteSwitchProgressBar
  },
  props: {
    useDarkMode: {
      type: Boolean,
      default: false
    },
    stickyHeader: {
      type: Boolean,
      default: false
    },
    showOnScrollUp: {
      type: Boolean,
      default: true
    }
  },
  emits: ['show-site-selector-drawer', 'search', 'edit-navigations', 'edit-pages', 'edit-site-css', 'time-travel', 'edit-header-style'],
  data () {
    return {
      drawerShown: false,

      settingMenuOrder: false,
      menuItemsForOrdering: [],

      checkoutData: this.$checkoutData

    }
  },

  context: ['currentUser'],
  configuration: ['currentSite', 'currentCircuit', 'navigations', 'voucherTypes', 'sites'],
  models: {
    orders: {
      orderItems: {
        ticket: {
          showing: 'default'
        }
      },
      force: true,
      variables () {
        return {
          state: 'not_draft',
          limit: 10,
          userId: this.currentUser?.id,
          noCount: true
        }
      }
    }
  },
  computed: {
    firstName () {
      return this.currentUser && this.currentUser.firstName
    },
    loggedIn () {
      return this.currentUser && this.currentUser.isRemote
    },
    navigationList () {
      const pageMenuItems = _.filter(this.navigations?.data, navigation => navigation.visible && !navigation.isArchived && (navigation.showInHeader || navigation.showInMenu))

      if (!pageMenuItems) return []

      return pageMenuItems
    },
    menuList () {
      if (this.$q.screen.lt.md) return groupByCategory(this.navigationList)

      const menuItems = this.navigationList.filter(navigation => navigation.visible && !navigation.showInHeader)

      return groupByCategory(menuItems)
    },
    purchasingMembership () {
      return this.$checkoutData?.purchasingMembership || this.$order?.purchasingMembership
    },
    showChangeLocationButton () {
      return _.filter(this.sites.data, site => !site.isLibrary && !site.consumerConfig.hideOnLocationSelectPage).length > 1
    },
    hasOtherIcons () {
      return (this.currentUser && this.orders.currentOrders.length > 0) || (this.currentSite.showCart && this.$order.draftOrderItemsCartCount > 0)
    },
    isDropdownButton () {
      return this.currentCircuit && this.currentCircuit.availableLanguagesOptions?.length > 2
    },

    styles () {
      const styles = []
      styles.push('width: 100%;')
      if (this.$q.screen.gt.xs) {
        styles.push('padding-left: 0')
        styles.push('padding-right: 0')
      }

      if (this.currentSite.consumerConfig.headerFullWidth && this.currentSite.consumerConfig.headerType !== HEADER_TYPES.DEFAULT) {
        styles.push('max-width: 100%')
      } else {
        styles.push(`max-width: ${this.$getMaxWidthForScreenSize()}`)
      }

      return styles.join(';')
    },
    showTopBar () {
      return this.currentSite?.consumerConfig?.headerType !== HEADER_TYPES.DEFAULT && (
        (this.currentSite?.consumerConfig?.headerShowSocialIcons && this.$q.screen.gt.xs) ||
        this.currentSite?.consumerConfig?.showSiteSelectorOnNavigations ||
        this.currentCircuit?.supportsTranslations
      )
    }
  },
  watch: {
    'voucherTypes.data': {
      handler () {
        this.$checkoutData.hasRedemptions = _.some(this.voucherTypes.data, voucherType => voucherType.isReward)
      },
      immediate: true
    }
  },
  created () {
    _.extend(this, { HEADER_TYPES })
  },
  methods: {
    toggleMainMenu () {
      if (this.drawerShown) {
        this.closeMainMenu()
      } else {
        this.openMainMenu()
      }
    },
    openMainMenu () {
      this.drawerShown = true
      this.$trackEvent(this.$customerEvents.OPENED_MAIN_MENU)
    },
    closeMainMenu () {
      this.drawerShown = false
      this.$trackEvent(this.$customerEvents.CLOSED_MAIN_MENU)
    }
  }
}
</script>

<style lang="scss" scoped>
  .centered-title-button {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: -64px;
    margin-top: -23px;
  }
  .drawer-content {
    background-color: rgba(255, 255, 255, 0.3);
    color: $dark;
    font-weight: bold;
  }

  .q-header {
    background-color: var(--q-header);
  }
  .bg-gradient-transparent {
    background: linear-gradient(to bottom, var(--q-header) 0%, transparent 100%);
  }

  @media (hover: hover) {
    .menu .q-item:hover, .menu .q-btn:hover {
      color: var(--q-secondary);
    }
  }

  .list-complete-item {
    transition: all 0.5s;
  }
  .list-complete-enter {
    opacity: 0;
    transform: translateX(0) translateY(100);
  }
  .list-complete-leave-to {
    opacity: 0;
    transform: translateX(0) translateY(-100);
  }
  .list-complete-leave-active {
    position: absolute;
  }
</style>

<style lang="scss">
  .bg-initial > .q-drawer {
    background: $background;
  }

  .q-toolbar__title {
    min-width: 230px;
  }

  .q-tab__label {
    font-size: 10px;
  }

  .size-md  .q-tab__label {
    font-size: 14px;
  }

  #consumer-layout-mobile {
    -moz-user-select: none;
    -khtml-user-select: none;
    -webkit-user-select: none;
  }

  .btn-link::after{
    font-family:"Material Icons";
    content:"\e5e1";
    font-size:14px;
    position:absolute;
    right:-15px;
    top:8px;
    transition-duration:0.1s;
    transition-timing-function:ease-in-out;
    transition-delay:0s;
    transition-property:all;
  }
  .btn-link.q-btn--dense::after{
    top:4px;
  }
  .btn-link:hover::after{
    right:-20px;
  }
</style>
